// import React from 'react';
import { ArrowRight, CheckCircle, TrendingUp, Shield, Zap, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { KaryvaLogoAnimated3D } from '../KaryvaLogoAnimated3D';

const APP_URL = 'https://app.karyva.com';

interface HomePageProps {
  onNavigate: (page: string) => void;
}

export function HomePage({ onNavigate }: HomePageProps) {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#2A3B4C] via-[#3E5365] to-[#556B7C] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-[#DAA520] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#3E5365] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-[#DAA520]/20 border border-[#DAA520]/40 rounded-full text-sm mb-6">
                <span className="text-[#FFD700]">PMI/PMBOK Aligned Platform</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl mb-6 leading-tight">
                Enterprise Project Portfolio Management
              </h1>
              
              <p className="text-xl text-[#D5D9DD] mb-8 leading-relaxed">
                Transform project delivery with a comprehensive PPM platform built for enterprise excellence. 
                Customizable, secure, and scalable solutions tailored to your organization.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <a
                  href={`${APP_URL}/signup`}
                  className="inline-flex items-center justify-center rounded-md bg-[#DAA520] hover:bg-[#B8860B] text-white px-8 py-4 text-lg font-medium transition-colors"
                >
                  Start free trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <Button
                  onClick={() => onNavigate('contact')}
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                >
                  Contact Karyva Team
                </Button>
                <Button
                  onClick={() => onNavigate('platform')}
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                >
                  Explore Platform
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="mt-12 pt-8 border-t border-white/20">
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <div className="text-3xl font-bold text-[#DAA520] mb-1">40%</div>
                    <div className="text-sm text-[#A8B5C3]">Project Success Improvement</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#DAA520] mb-1">30%</div>
                    <div className="text-sm text-[#A8B5C3]">Governance Overhead Reduction</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#DAA520] mb-1">50+</div>
                    <div className="text-sm text-[#A8B5C3]">Concurrent Projects Supported</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Logo Animation */}
            <div className="hidden md:flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-[#DAA520] opacity-20 blur-3xl rounded-full"></div>
                <KaryvaLogoAnimated3D size="hero" darkBg={true} autoPlay={true} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl text-[#2A3B4C] mb-4">Why Leading Enterprises Choose Karyva</h2>
            <p className="text-xl text-[#556B7C] max-w-3xl mx-auto">
              Purpose-built for organizations that demand excellence in project portfolio management
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <ValueCard
              icon={Shield}
              title="Enterprise-Grade Security"
              description="Single-tenant AWS deployments with complete data sovereignty and SOC 2 compliance"
            />
            <ValueCard
              icon={Zap}
              title="Rapid Deployment"
              description="Go live in weeks, not months, with pre-configured PMI-compliant templates"
            />
            <ValueCard
              icon={TrendingUp}
              title="Measurable ROI"
              description="Average 40% improvement in project success rates with comprehensive analytics"
            />
            <ValueCard
              icon={Globe}
              title="Global Scalability"
              description="Support unlimited users and projects across multiple regions worldwide"
            />
          </div>
        </div>
      </section>

      {/* Platform Overview */}
      <section className="py-20 bg-[#E8E9E9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl text-[#2A3B4C] mb-6">
                Comprehensive Project Portfolio Management
              </h2>
              <p className="text-lg text-[#556B7C] mb-8 leading-relaxed">
                Karyva brings together portfolio management, program oversight, and project execution 
                in a unified, intelligent platform built on PMI/PMBOK principles.
              </p>
              
              <div className="space-y-4">
                <Feature text="8 integrated modules covering every aspect of PPM" />
                <Feature text="AI-powered assistants for risk, quality, and governance" />
                <Feature text="5-gate approval process with 29 PMI-compliant templates" />
                <Feature text="Multi-level RAID log management across all levels" />
                <Feature text="Real-time dashboards and business intelligence" />
                <Feature text="Complete audit trails and version control" />
              </div>

              <Button
                onClick={() => onNavigate('platform')}
                className="mt-8 bg-[#DAA520] hover:bg-[#B8860B] text-white"
              >
                Explore Features
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1676276374782-39159bc5e7b4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9qZWN0JTIwbWFuYWdlbWVudCUyMGRhc2hib2FyZHxlbnwxfHx8fDE3NjcxMTI0MzF8MA&ixlib=rb-4.1.0&q=80&w=1080"
                alt="Project Management Dashboard"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl text-[#2A3B4C] mb-4">Built for the way your industry actually works</h2>
            <p className="text-xl text-[#556B7C] max-w-3xl mx-auto">
              Deep workflows for the verticals we serve — not a blank Kanban board pretending to fit every team.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <IndustryCard
              name="Construction"
              tagline="Submittals, RFIs, inspections, punch lists"
              onClick={() => onNavigate('for-construction')}
            />
            <IndustryCard
              name="Legal"
              tagline="Matters, deadlines, audit trails"
              onClick={() => onNavigate('for-legal')}
            />
            <IndustryCard
              name="Healthcare"
              tagline="Accreditation, multi-site, compliance"
              onClick={() => onNavigate('for-healthcare')}
            />
            <IndustryCard
              name="Marketing & Agency"
              tagline="Retainers, campaigns, client portals"
              onClick={() => onNavigate('for-marketing')}
            />
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => onNavigate('solutions')}
              variant="outline"
              className="border-[#DAA520] text-[#DAA520] hover:bg-[#FFF9E6]"
            >
              View all industry solutions
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#2A3B4C] to-[#3E5365] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl mb-6">Ready to Transform Your Project Portfolio?</h2>
          <p className="text-xl text-[#D5D9DD] mb-8">
            Join leading enterprises who trust Karyva to deliver project excellence at scale
          </p>
          <Button
            onClick={() => onNavigate('contact')}
            className="bg-[#DAA520] hover:bg-[#B8860B] text-white px-8 py-6 text-lg"
          >
            Contact Karyva Team
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}

// Helper Components
function ValueCard({ icon: Icon, title, description }: { icon: any; title: string; description: string }) {
  return (
    <div className="p-6 bg-[#E8E9E9] rounded-lg border border-[#C4CDD5] hover:shadow-lg transition-shadow">
      <div className="w-12 h-12 bg-[#DAA520] rounded-lg flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-white" />
      </div>
      <h3 className="text-lg text-[#2A3B4C] mb-2">{title}</h3>
      <p className="text-sm text-[#556B7C] leading-relaxed">{description}</p>
    </div>
  );
}

function Feature({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-3">
      <CheckCircle className="w-5 h-5 text-[#DAA520] flex-shrink-0 mt-0.5" />
      <span className="text-[#556B7C]">{text}</span>
    </div>
  );
}

function IndustryCard({
  name,
  tagline,
  onClick,
}: {
  name: string;
  tagline?: string;
  onClick?: () => void;
}) {
  const interactive = typeof onClick === 'function';
  const baseClass =
    'p-6 bg-[#E8E9E9] rounded-lg border border-[#C4CDD5] text-left transition-colors';
  const interactiveClass = interactive
    ? 'hover:border-[#DAA520] hover:bg-white cursor-pointer w-full'
    : 'hover:border-[#DAA520]';

  const body = (
    <>
      <p className="text-[#2A3B4C] font-medium mb-1">{name}</p>
      {tagline && <p className="text-sm text-[#556B7C]">{tagline}</p>}
      {interactive && (
        <div className="mt-4 text-sm text-[#DAA520] flex items-center gap-1">
          See the workflow
          <ArrowRight className="w-4 h-4" />
        </div>
      )}
    </>
  );

  if (interactive) {
    return (
      <button onClick={onClick} className={`${baseClass} ${interactiveClass}`}>
        {body}
      </button>
    );
  }
  return <div className={`${baseClass} ${interactiveClass}`}>{body}</div>;
}